import React, { useState } from 'react';
import moment from 'moment';
import 'moment/locale/ru';
import PropTypes from 'prop-types';
import { withStyles, makeStyles, lighten } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TableFooter from '@material-ui/core/TableFooter';
import TablePagination from '@material-ui/core/TablePagination';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import TablePaginationActions from '../../components/pagination';
import { SHARE_TYPES } from '../../enum/share-types';

import { useAPI } from './use-api';

moment.locale('ru');

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: '1 1 100%',
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();

  return (
    <Toolbar>
      <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
        Паевые земельные участки
      </Typography>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: '#F0F0F0',
    color: theme.palette.common.black,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {},
  },
}))(TableRow);

const useStyles = makeStyles({
  table: {
    minWidth: 700,
  },
});

const headCells = [
  { id: 'audit.createdDate', numeric: false, disablePadding: false, label: 'Дата создания' },
  { id: 'privateType', numeric: false, disablePadding: false, label: 'Тип собственности' },
  { id: 'cadastral', numeric: false, disablePadding: false, label: 'Кадастровый номер' },
  { id: 'calculatedArea', numeric: true, disablePadding: false, label: 'Площадь' },
  { id: 'contract.signingDate', numeric: false, disablePadding: false, label: 'Дата подписания' },
  { id: 'contract.endDate', numeric: false, disablePadding: false, label: 'Дата окончания' },
  { id: 'contractor', numeric: false, disablePadding: false, label: 'Контрагент' },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'right'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

export const Shares = () => {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(8);

  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('audit.createdDate');

  const { shares, isLoading } = useAPI({ page, size, orderBy, order });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <>
      <EnhancedTableToolbar />
      <TableContainer>
        <Table className={classes.table} stickyHeader aria-label="sticky table">
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />

          <TableBody>
            {shares.content.map((row) => (
              <StyledTableRow hover key={row.id}>
                <StyledTableCell align="right">
                  {moment(row.createdDate).format('LL')}
                </StyledTableCell>
                <StyledTableCell align="right">{SHARE_TYPES[row.propertyType]}</StyledTableCell>
                <StyledTableCell align="right">{row.cadastral}</StyledTableCell>
                <StyledTableCell align="right">{row.calculatedArea.toFixed(2)}</StyledTableCell>
                <StyledTableCell align="right">{row.contract?.signingDate}</StyledTableCell>
                <StyledTableCell align="right">{row.contract?.endDate}</StyledTableCell>
                <StyledTableCell align="right">{row.contract?.contractor?.name}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[8, 10, 25, { label: 'All', value: -1 }]}
                colSpan={7}
                count={shares.totalElements}
                rowsPerPage={size}
                page={page}
                SelectProps={{
                  inputProps: { 'aria-label': 'rows per page' },
                  native: true,
                }}
                onPageChange={(e, page) => setPage(page)}
                onRowsPerPageChange={(e) => {
                  setSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </>
  );
};
